// CodeMirror 6 extension that decorates `{{NAME}}` tokens inline as
// chips AND offers env-var autocomplete so the user can type the bare
// variable name (no `{{ }}`) and get a chip — same behavior as the URL
// and Params editors.
//
// Usage:
//   varHighlight({ isResolved, resolveValue, envVarNames, vars })
//
// The extension ships:
//   • Inline decorations for completed `{{NAME}}` tokens (with hover
//     tooltips showing the resolved value)
//   • A completion source that fires when the user types either a bare
//     letter or `{{` — picking a suggestion inserts `{{NAME}}`.
// Use editor `class="cm-host"` styles in styles.css for the visual
// treatment of the chips.

import { Decoration, EditorView, ViewPlugin, type DecorationSet, type ViewUpdate } from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
import { autocompletion, type CompletionContext, type CompletionResult } from "@codemirror/autocomplete";

export interface VarHighlightOptions {
  isResolved?: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
  /** Known env-var names from across the workspace (suggested in autocomplete). */
  envVarNames?: string[];
  /** Name → value lookup; used to show the resolved value next to each suggestion. */
  vars?: Record<string, string>;
}

// Matches `{{ANY_VARIABLE-ish}}` with optional whitespace padding.
const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;

export function varHighlight(opts: VarHighlightOptions = {}) {
  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) { this.decorations = build(view, opts); }
      update(u: ViewUpdate) {
        if (u.docChanged || u.viewportChanged) {
          this.decorations = build(u.view, opts);
        }
      }
    },
    { decorations: (v) => v.decorations },
  );
  return [
    plugin,
    varHighlightTheme,
    autocompletion({
      override: [makeCompletionSource(opts)],
      activateOnTyping: true,
      closeOnBlur: true,
    }),
  ];
}

// Build a completion source that fires in two cases:
//   1. Bare word: `HO` → suggest HOST, picking inserts `{{HOST}}`.
//   2. Inside braces: `{{HO` → suggest HOST, picking completes the
//      braces to `{{HOST}}` and leaves the cursor right after `}}`.
// Either way the result is a fully-formed chip-eligible token.
function makeCompletionSource(opts: VarHighlightOptions) {
  return (ctx: CompletionContext): CompletionResult | null => {
    const names = opts.envVarNames ?? [];
    if (names.length === 0) return null;
    const vals = opts.vars ?? {};

    // Look back from the cursor: are we inside `{{…|`? If yes, complete
    // the inside; otherwise treat the trailing identifier as a candidate.
    const before = ctx.state.doc.sliceString(Math.max(0, ctx.pos - 64), ctx.pos);

    // Case 1: inside braces. Match `{{` followed by an identifier-ish run.
    const inside = /\{\{\s*([a-zA-Z0-9_.-]*)$/.exec(before);
    if (inside) {
      const start = ctx.pos - inside[1].length;
      return {
        from: start,
        validFor: /^[a-zA-Z0-9_.-]*$/,
        options: names.map((n) => ({
          label: n,
          detail: vals[n] ? truncate(vals[n], 36) : undefined,
          type: "variable",
          // Apply: replace the partial identifier AND any open `{{`, plus
          // the close braces if they aren't already there.
          apply: (view, _completion, from, to) => {
            const afterPos = to;
            const after = view.state.doc.sliceString(afterPos, Math.min(view.state.doc.length, afterPos + 2));
            const insert = `${n}${after === "}}" ? "" : "}}"}`;
            view.dispatch({
              changes: { from, to, insert },
              selection: { anchor: from + insert.length + (after === "}}" ? 2 : 0) },
            });
          },
        })),
      };
    }

    // Case 2: bare word that matches an env var name. Only fire when
    // the trailing characters look like an identifier and at least one
    // env var starts with the same prefix — otherwise we'd pop the
    // suggest list on every keystroke inside random JSON keys.
    const bare = /([A-Za-z_][A-Za-z0-9_]*)$/.exec(before);
    if (!bare) return null;
    if (ctx.explicit === false && bare[1].length < 1) return null;
    const word = bare[1];
    const matches = names.filter((n) => n.toLowerCase().startsWith(word.toLowerCase()));
    if (matches.length === 0) return null;
    const start = ctx.pos - word.length;
    return {
      from: start,
      // Restrict the active completion to identifier-shaped characters
      // so the popover dismisses cleanly when the user types `:`, `,`, etc.
      validFor: /^[A-Za-z_][A-Za-z0-9_]*$/,
      options: matches.map((n) => ({
        label: n,
        detail: vals[n] ? truncate(vals[n], 36) : "env var",
        type: "variable",
        // Replace the bare word with a full `{{NAME}}` token.
        apply: `{{${n}}}`,
      })),
    };
  };
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

function build(view: EditorView, opts: VarHighlightOptions): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.sliceDoc(from, to);
    const re = new RegExp(TOKEN_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const start = from + m.index;
      const end = start + m[0].length;
      const name = m[1];
      const missing = opts.isResolved ? !opts.isResolved(name) : false;
      const value = opts.resolveValue ? opts.resolveValue(name) : undefined;
      const tooltip = missing
        ? `Unresolved · ${name}`
        : value != null && value !== ""
          ? `${name} = ${value}`
          : name;
      builder.add(
        start,
        end,
        Decoration.mark({
          class: `cm-var-chip${missing ? " is-missing" : ""}`,
          attributes: { "data-tooltip": tooltip, title: tooltip },
        }),
      );
    }
  }
  return builder.finish();
}

// Minimal scoped theme — most of the chip look lives in styles.css so
// it matches the ChipInput pills used elsewhere. We only set the
// inline-flex baseline here so the chip layouts the same as text.
const varHighlightTheme = EditorView.baseTheme({
  ".cm-var-chip": {
    borderRadius: "5px",
    padding: "0 6px",
    fontWeight: "600",
  },
});
